import * as React from 'react'

import { useI18n, useSiteConfig } from '@thg-commerce/enterprise-core'
import { SafeHtml } from '@thg-commerce/gravity-elements'
import { Tabs } from '@thg-commerce/gravity-patterns'
import { TabProps } from '@thg-commerce/gravity-patterns/Tabs/Tabs'

import {
  BoldDescription,
  SecondaryTitle,
  SectionContainer,
  Subtitle,
  TabContainer,
  Title,
  TitleContainer,
} from './styles'

const sectionIdSuffix = 'tab-section'

export const deliveryAndReturnsModalSectionIds = {
  delivery: `delivery-${sectionIdSuffix}`,
  clickAndCollect: `click-and-collect-${sectionIdSuffix}`,
  returns: `returns-${sectionIdSuffix}`,
}

interface DeliveryReturnsModalProps {
  tabIndex?: number
  focusedSectionId?: string
  setTabs?: React.Dispatch<React.SetStateAction<TabProps[] | undefined>>
  dropshipRichContent?: {
    deliveryInformation: string
    returnsInformation: string
  }
}

export const DeliveryReturnsModal = (props: DeliveryReturnsModalProps) => {
  const i18n = useI18n()
  const { setTabs, ...otherProps } = props
  const { focusedSectionId } = props
  const {
    showClickAndCollectProductInfoBox,
    showClickAndCollectProductTab,
    showDropShipReturnText,
  } = useSiteConfig()

  const deliveryRef = React.useRef<HTMLDivElement>(null)
  const clickAndCollectRef = React.useRef<HTMLDivElement>(null)
  const returnsRef = React.useRef<HTMLDivElement>(null)

  const refs = React.useMemo(() => {
    return {
      [deliveryAndReturnsModalSectionIds.delivery]: deliveryRef,
      [deliveryAndReturnsModalSectionIds.clickAndCollect]: clickAndCollectRef,
      [deliveryAndReturnsModalSectionIds.returns]: returnsRef,
    }
  }, [])

  React.useEffect(() => {
    if (focusedSectionId) refs[focusedSectionId].current?.scrollIntoView()
  }, [focusedSectionId, refs])

  const warehouseI18nText = {
    deliveryTitle: i18n('product.infoboxes.modal.delivery.title'),
    returnsTitle: i18n('product.infoboxes.modal.returns.title'),
    freeStandardDeliveryTitle: i18n(
      'product.infoboxes.modal.delivery.freestandard.title',
    ),
    freeStandardDeliverySubtitle: i18n(
      'product.infoboxes.modal.delivery.freestandard.subtitle',
    ),
    freeStandardDeliveryDescription: i18n(
      'product.infoboxes.modal.delivery.freestandard.description',
    ),
    standardDeliveryTitle: i18n(
      'product.infoboxes.modal.delivery.standard.title',
    ),
    standardDeliverySubtitle: i18n(
      'product.infoboxes.modal.delivery.standard.subtitle',
    ),
    standardDeliveryDescription: i18n(
      'product.infoboxes.modal.delivery.standard.description',
    ),
    clickAndCollectDeliveryTitle: i18n(
      'product.infoboxes.modal.delivery.clickandcollect.title',
    ),
    clickAndCollectDeliverySubtitle: i18n(
      'product.infoboxes.modal.delivery.clickandcollect.subtitle',
    ),
    clickAndCollectDeliveryDescription: i18n(
      'product.infoboxes.modal.delivery.clickandcollect.description',
    ),
    moreInfoText: i18n('product.infoboxes.modal.moreinfo.text'),
    moreInfoLink: i18n('product.infoboxes.modal.moreinfo.link'),
    returnsPolicyTitle: i18n('product.infoboxes.modal.returns.policy.title'),
    returnsPolicyPleaseNote: i18n(
      'product.infoboxes.modal.returns.policy.pleasenote',
    ),
    returnsPolicyDescription: i18n(
      'product.infoboxes.modal.returns.policy.description',
    ),
    returnOrExchangeInStoreTitle: i18n(
      'product.infoboxes.modal.returns.instore.title',
    ),
    returnOrExchangeInStoreSubtitle: i18n(
      'product.infoboxes.modal.returns.instore.subtitle',
    ),
    returnOrExchangeInStoreDescription: i18n(
      'product.infoboxes.modal.returns.instore.description',
    ),
    returnsSaleItemsTitle: i18n(
      'product.infoboxes.modal.returns.instore.saleitems.title',
    ),
    returnsSaleItemsDescription: i18n(
      'product.infoboxes.modal.returns.instore.saleitems.description',
    ),
    returnsOnlineTitle: i18n(
      'product.infoboxes.modal.returns.instore.online.title',
    ),
    returnsOnlineDescription: i18n(
      'product.infoboxes.modal.returns.instore.online.description',
    ),
  }

  const dropshipI18nText = {
    deliveryTitle: i18n('product.infoboxes.modal.dropship.delivery.title'),
    returnsTitle: i18n('product.infoboxes.modal.dropship.returns.title'),
    dropshipReturnText: i18n('product.infoboxes.modal.dropship.return.text'),
  }

  const ClickAndCollectContent = (
    <SectionContainer
      id={deliveryAndReturnsModalSectionIds.clickAndCollect}
      ref={refs[deliveryAndReturnsModalSectionIds.clickAndCollect]}
    >
      <TitleContainer>
        <Title>{warehouseI18nText.clickAndCollectDeliveryTitle}</Title>
        <Subtitle useSuccessColor>
          {warehouseI18nText.clickAndCollectDeliverySubtitle}
        </Subtitle>
      </TitleContainer>
      <SafeHtml
        content={warehouseI18nText.clickAndCollectDeliveryDescription}
      />
      {warehouseI18nText.moreInfoLink && (
        <a href={warehouseI18nText.moreInfoLink}>
          {warehouseI18nText.moreInfoText}
        </a>
      )}
    </SectionContainer>
  )

  const ClickAndCollectTab = (
    <TabContainer>{ClickAndCollectContent}</TabContainer>
  )

  const DeliveryTab = otherProps.dropshipRichContent ? (
    <TabContainer id={deliveryAndReturnsModalSectionIds.delivery}>
      <SectionContainer ref={refs[deliveryAndReturnsModalSectionIds.delivery]}>
        <TitleContainer>
          <Title>{dropshipI18nText.deliveryTitle}</Title>
        </TitleContainer>
        <SafeHtml
          content={otherProps.dropshipRichContent.deliveryInformation}
        />
      </SectionContainer>
    </TabContainer>
  ) : (
    <TabContainer id={deliveryAndReturnsModalSectionIds.delivery}>
      <SectionContainer ref={refs[deliveryAndReturnsModalSectionIds.delivery]}>
        <TitleContainer>
          <Title>{warehouseI18nText.freeStandardDeliveryTitle}</Title>
          <Subtitle useSuccessColor>
            {warehouseI18nText.freeStandardDeliverySubtitle}
          </Subtitle>
        </TitleContainer>
        <SafeHtml content={warehouseI18nText.freeStandardDeliveryDescription} />
      </SectionContainer>
      <SectionContainer>
        <TitleContainer>
          <Title>{warehouseI18nText.standardDeliveryTitle}</Title>
          <Subtitle>{warehouseI18nText.standardDeliverySubtitle}</Subtitle>
        </TitleContainer>
        <p>{warehouseI18nText.standardDeliveryDescription}</p>
        {warehouseI18nText.moreInfoLink && (
          <a href={warehouseI18nText.moreInfoLink}>
            {warehouseI18nText.moreInfoText}
          </a>
        )}
      </SectionContainer>
      {!showClickAndCollectProductTab &&
        showClickAndCollectProductInfoBox &&
        ClickAndCollectContent}
    </TabContainer>
  )

  const ReturnsTab = otherProps.dropshipRichContent ? (
    <TabContainer id={deliveryAndReturnsModalSectionIds.returns}>
      <SectionContainer ref={refs[deliveryAndReturnsModalSectionIds.returns]}>
        <TitleContainer>
          <Title>{dropshipI18nText.returnsTitle}</Title>
        </TitleContainer>
        {showDropShipReturnText && (
          <SafeHtml content={dropshipI18nText.dropshipReturnText} />
        )}
        <SafeHtml content={otherProps.dropshipRichContent.returnsInformation} />
      </SectionContainer>
    </TabContainer>
  ) : (
    <TabContainer id={deliveryAndReturnsModalSectionIds.returns}>
      <SectionContainer ref={refs[deliveryAndReturnsModalSectionIds.returns]}>
        <TitleContainer>
          <Title>{warehouseI18nText.returnsPolicyTitle}</Title>
        </TitleContainer>
        <BoldDescription>
          {warehouseI18nText.returnsPolicyPleaseNote}
        </BoldDescription>
        <SafeHtml content={warehouseI18nText.returnsPolicyDescription} />
      </SectionContainer>
      <SectionContainer>
        <TitleContainer>
          <Title>{warehouseI18nText.returnOrExchangeInStoreTitle}</Title>
          <Subtitle useSuccessColor>
            {warehouseI18nText.returnOrExchangeInStoreSubtitle}
          </Subtitle>
        </TitleContainer>
        <SafeHtml
          content={warehouseI18nText.returnOrExchangeInStoreDescription}
        />
        <SecondaryTitle>
          {warehouseI18nText.returnsSaleItemsTitle}
        </SecondaryTitle>
        <p>{warehouseI18nText.returnsSaleItemsDescription}</p>
        <SecondaryTitle>{warehouseI18nText.returnsOnlineTitle}</SecondaryTitle>
        <p>{warehouseI18nText.returnsOnlineDescription}</p>
      </SectionContainer>
    </TabContainer>
  )

  const [tabs] = React.useState<TabProps[]>([
    {
      id: deliveryAndReturnsModalSectionIds.delivery,
      title: warehouseI18nText.deliveryTitle,
      content: DeliveryTab,
    },
    ...(showClickAndCollectProductTab && !otherProps.dropshipRichContent
      ? [
          {
            id: deliveryAndReturnsModalSectionIds.clickAndCollect,
            title: warehouseI18nText.clickAndCollectDeliveryTitle,
            content: ClickAndCollectTab,
          },
        ]
      : []),
    {
      id: deliveryAndReturnsModalSectionIds.returns,
      title: warehouseI18nText.returnsTitle,
      content: ReturnsTab,
    },
  ])

  React.useEffect(() => {
    if (setTabs) setTabs(tabs)
  }, [tabs, setTabs])

  return <Tabs tabIndex={otherProps.tabIndex} tabs={tabs} />
}
